/**
 * AdminUserNotesPage (Iter 9.40 Pass A.9)
 *
 * Aggregated timeline of every note about a user across the platform.
 * Reads `GET /api/admin/users/{user_id}/notes-rollup`.
 *
 * Sources merged: pilot intake long-text · per-category preference notes ·
 * host_notes the user authored · admin internal notes · user.admin_annotations.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const SOURCE_BADGE = {
  pilot_intake: "bg-purple-50 text-purple-700",
  preferences: "bg-pink-50 text-pink-700",
  host_notes: "bg-amber-50 text-amber-700",
  admin: "bg-blue-50 text-blue-700",
  user_annotation: "bg-emerald-50 text-emerald-700",
  user_field: "bg-gray-100 text-gray-600",
};

export default function AdminUserNotesPage() {
  const { userId } = useParams();
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/users/${userId}/notes-rollup`, { headers: auth() });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData({ items: [], error: "Could not load" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  return (
    <div className="min-h-screen bg-white" data-testid="admin-user-notes-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/admin/users" className="text-xs text-gray-500 inline-flex items-center gap-1 hover:text-black">
          <ArrowLeft className="w-3 h-3" /> Back to People
        </Link>
        <h1 className="text-2xl font-semibold mt-2">Notes timeline</h1>
        <p className="text-xs text-gray-500 mb-4">
          Every note about <span className="font-mono">{userId}</span>, aggregated and sorted newest first.
        </p>

        {loading ? (
          <div className="text-xs text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> loading…</div>
        ) : (
          <>
            <div className="text-[11px] text-gray-400 mb-3">
              {data.count ?? data.items?.length ?? 0} entries
            </div>
            <div className="space-y-2">
              {(data.items || []).map((it, i) => (
                <div
                  key={i}
                  className="border rounded-lg p-3 bg-white"
                  data-testid={`notes-item-${i}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${SOURCE_BADGE[it.source] || "bg-gray-100"} text-[10px] uppercase tracking-wide`}>
                      {it.source}
                    </Badge>
                    <div className="text-xs font-medium text-gray-800">{it.label}</div>
                    <div className="flex-1" />
                    {it.at && <div className="text-[10px] text-gray-400">{new Date(it.at).toLocaleString()}</div>}
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap">{it.body || "-"}</div>
                </div>
              ))}
              {(data.items || []).length === 0 && (
                <div className="text-xs text-gray-400 italic">No notes yet.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
